/**
 * LiveDash — /livedash
 *
 * Every lead, the moment it lands. One Firestore collectionGroup subscription
 * on `leads` ordered by createdAt drives the whole screen.
 *
 * Order on createdAt, NOT savedAt: the pipeline writes createdAt
 * (FieldValue.serverTimestamp) on every lead. `savedAt` is a legacy field the
 * current writer stopped setting, so ordering on it returns nothing newer
 * than Nov 2025.
 *
 * Leads live at {rootCollection}/{scrapeId}/leads/{leadId} — the market is on
 * the PATH, not the document, which is why marketFromPath is the single
 * source of truth for attribution.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collectionGroup, getCountFromServer, limit, onSnapshot, orderBy, query, where, Timestamp,
} from 'firebase/firestore';
import { AnimatePresence, motion } from 'framer-motion';
import { db } from '../firebase';
import leadfeedLogo from '../assets/clients/leadfeed.png';

// Sized to hold a FULL 24 hours, because the hot-market ranking and the
// service mix are computed from this buffer. At 400 it only reached back ~20h
// once all 12 Michael & Son zones were live (504 leads/24h), silently
// undercounting every market. Revisit if daily volume approaches this.
const FEED_LIMIT = 1500;
const FLASH_MS = 8000;

// Firestore orders by TYPE before value, and map > timestamp. Legacy
// `scrapes/*/leads` docs carry `createdAt: {}` (an empty map), so on a plain
// descending orderBy they sort ABOVE every real lead and swallow the window.
// A range filter is type-constrained, so comparing against a Timestamp keeps
// only timestamp-typed values. Bound is far-future rather than now() so leads
// created after we subscribe still stream in live.
const TS_TYPE_BOUND = Timestamp.fromDate(new Date('2100-01-01T00:00:00Z'));

// 31 days is ~11k leads — far past the live buffer, so it gets its own
// server-side aggregation instead of being counted client-side. A count query
// bills ~1 read per 1000 matched docs, so this is cheap to refresh.
const DAY_MS = 864e5;
const MONTH_MS = 31 * 864e5;
const MONTH_REFRESH_MS = 5 * 60 * 1000;
const DAILY_DAYS = 28;          // 4 weeks, so each weekday has 4 samples to average
const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
// The scrapers only run 6am-11pm (every nd50 cron is `6-23`), so projecting today's
// total from the fraction of the CALENDAR day elapsed reads far too low all morning:
// at 10am the clock says 42% of the day is gone but only 24% of the scraping window
// has been worked. Project against the working window instead.
const SCRAPE_START_HR = 6;
const SCRAPE_END_HR = 23;
// Read the hour off the CLOCK rather than subtracting midnight. On a DST
// changeover day those two disagree: on 2026-11-01 the clock went back an hour
// at 2am, so 13.4 real hours had passed while the clock read 12:26. Elapsed-time
// maths put the day 43% worked when the scrapers had done 38% of their window,
// which deflated the projection by the same ~13%.
const scrapeDayFraction = (nowMs) => {
  const d = new Date(nowMs);
  const hoursIn = d.getHours() + d.getMinutes() / 60;
  return Math.min(1, Math.max(0, (hoursIn - SCRAPE_START_HR) / (SCRAPE_END_HR - SCRAPE_START_HR)));
};

// Same wall-clock time of day on an earlier day. setHours is DST-aware, so this
// lands on 12:26 local whatever that day's UTC offset was; adding a fixed number
// of milliseconds does not.
const sameClockTime = (dayStartMs, ref) => {
  const d = new Date(dayStartMs);
  d.setHours(ref.getHours(), ref.getMinutes(), ref.getSeconds(), ref.getMilliseconds());
  return d.getTime();
};

// The following local midnight. A day is not always 864e5 ms long — the two DST
// days each year are 23h and 25h — so a full-day window has to be built from the
// calendar, not by adding a day's worth of milliseconds.
const nextMidnight = (dayStartMs) => {
  const d = new Date(dayStartMs);
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// How far the clock and the stopwatch disagree about today, in minutes: 0 on an
// ordinary day, ±60 on a changeover. Surfaced in the UI so a reader knows the
// comparison was clock-aligned rather than wondering why it moved.
const clockDriftMin = (nowMs, dayStartMs) =>
  Math.round(((nowMs - dayStartMs) / 60e3) - (new Date(nowMs).getHours() * 60 + new Date(nowMs).getMinutes()));

/* ── brand ──────────────────────────────────────────────────────────────── */
// #007818 is the logo green, sampled from the mark; the neon is that same hue
// lifted for legibility on black.
const G = {
  neon: '#00E85F',
  brand: '#007818',
  cyan: '#22D3EE',
  amber: '#FFC53D',
  text: '#E6FFEE',
  mute: '#5F8570',
  faint: '#2C4A38',
};

/* ── market attribution ─────────────────────────────────────────────────── */
// Longest prefix first: "newscrapesCharlotte" must not swallow
// "newscrapesCharlottesville" — two separate Michael & Son markets.
// The same trap caught DMV MD/DC: with no entry of its own, the bare "newscrapes"
// catch-all at the bottom matched it and ~1,300 of its leads were counted as Memphis,
// which made Memphis look like it did 66 leads/day on 9 accounts when the real split is
// ~33 for Memphis and ~33 for DMV MD/DC's 12. Any NEW market collection needs a line here.
const ROOT_TO_MARKET = [
  ['newscrapescharlottesville', 'Charlottesville'],
  ['newscrapescharlotte',       'Charlotte'],
  ['newscrapesdmvmddc',         'DMV MD/DC'],
  ['newscrapesdmvva',          'DMV VA'],
  ['newscrapesfredericksburg',  'Fredericksburg'],
  ['newscrapeswilmingtonnc',    'Wilmington NC'],
  ['newscrapesmyrtlebeach',     'Myrtle Beach'],
  ['newscrapesgreensboro',      'Greensboro'],
  ['newscrapeslosangeles',      'Los Angeles'],
  ['newscrapesconnecticut',     'Connecticut'],
  ['newscrapeslongisland',      'Long Island'],
  ['newscrapespanhandle',       'Panhandle'],
  ['newscrapesnashville',       'Nashville'],
  ['newscrapesbaltimore',       'Baltimore'],
  ['newscrapesrichmond',        'Richmond'],
  ['newscrapeshouston',         'Houston'],
  ['newscrapesftmyers',         'Ft Myers'],
  ['newscrapesorlando',         'Orlando'],
  ['newscrapesatlanta',         'Atlanta'],
  ['newscrapesraleigh',         'Raleigh'],
  ['newscrapessouthfl',         'South FL'],
  ['newscrapesnorfolk',         'Norfolk'],
  ['newscrapesdenver',          'Denver'],
  ['newscrapesdallas',          'Dallas'],
  ['newscrapesmiami',           'Miami'],
  ['newscrapestampa',           'Tampa'],
  ['newscrapesva',              'Northern Virginia'],
  ['newscrapes',                'Memphis'],
];
function marketFromPath(path = '') {
  const root = String(path).split('/')[0].toLowerCase();
  for (const [prefix, label] of ROOT_TO_MARKET) if (root.startsWith(prefix)) return label;
  return 'Unknown';
}

/* ── client attribution ─────────────────────────────────────────────────── */
// Mirrors clientForLead() in scraper-functions/index.js. Leads written from
// 2026-09-08 carry a stored `client`; anything older is classified here so the
// filter still works over history.
const MS_MARKETS = new Set([
  'Raleigh', 'Baltimore', 'Fredericksburg', 'Richmond', 'Norfolk',
  'Greensboro', 'Wilmington NC', 'Myrtle Beach', 'Charlotte', 'Charlottesville',
  'DMV VA', 'DMV MD/DC',
]);
const SWIFTPRO_LANES = new Set(['HVAC', 'Plumbing']);
const CLIENT_LABEL = { SwiftPro: 'SwiftPro', MichaelAndSon: 'Michael & Son' };
// Distinct from the green chrome and the amber phone tag, so which client owns
// a lead is readable at a glance — this matters most in Northern Virginia,
// where both clients' leads sit side by side in the same feed.
const CLIENT_STYLE = {
  SwiftPro:      { label: 'SWIFTPRO', color: '#22D3EE' },
  MichaelAndSon: { label: 'M & SON',  color: '#A78BFA' },
};

function clientOf(l) {
  if (l.client) return l.client;
  const svc = l.serviceType || l.vertical;
  // NOVA is shared ground: SwiftPro holds HVAC + Plumbing, Michael & Son the rest
  if (l.market === 'Northern Virginia') return SWIFTPRO_LANES.has(svc) ? 'SwiftPro' : 'MichaelAndSon';
  if (MS_MARKETS.has(l.market)) return 'MichaelAndSon';
  return null;
}

/* ── helpers ────────────────────────────────────────────────────────────── */
const ms = (v) => (v?.toMillis ? v.toMillis() : typeof v === 'number' ? v : 0);

// The scraper never fills `phones[]` — it leaves the number in the post body,
// which is what the lead email scans too.
const PHONE_RX = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;
function phoneOf(l) {
  if (Array.isArray(l.phones) && l.phones.length) return l.phones[0];
  const m = String(l.text || '').match(PHONE_RX);
  return m ? m[0].trim() : null;
}

function ago(t, now) {
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 10) return 'now';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };

function useCountUp(value, duration = 700) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const a = from.current, b = value;
    if (a === b) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setShown(Math.round(a + (b - a) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick); else from.current = b;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return shown;
}

/* ── page ───────────────────────────────────────────────────────────────── */
export default function LiveDash() {
  const [leads, setLeads] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [flash, setFlash] = useState(() => new Set());
  const [marketFilter, setMarketFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [lastAt, setLastAt] = useState(null);
  const [month, setMonth] = useState(null);
  const [dayExact, setDayExact] = useState(null);
  const [daily, setDaily] = useState([]);
  const [peers, setPeers] = useState([]);
  const [clientWeek, setClientWeek] = useState(null);
  const seeded = useRef(false);

  useEffect(() => {
    const q = query(
      collectionGroup(db, 'leads'),
      where('createdAt', '<=', TS_TYPE_BOUND),
      orderBy('createdAt', 'desc'),
      limit(FEED_LIMIT)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, path: d.ref.path, ...d.data() }))
          .filter((r) => r.path.split('/').length === 4 && r.path.startsWith('newscrapes'))
          .map((r) => ({
            ...r,
            market: marketFromPath(r.path),
            at: ms(r.createdAt) || r.capturedAt || r.newLeadAt || ms(r.savedAt) || r.postedAt || 0,
            phone: phoneOf(r),
            clientId: clientOf({ ...r, market: marketFromPath(r.path) }),
          }));

        const byKey = new Map();
        for (const r of rows) {
          const k = r.postId || r.permalink || r.id;
          if (!byKey.has(k) || r.at > byKey.get(k).at) byKey.set(k, r);
        }
        const next = [...byKey.values()].sort((a, b) => b.at - a.at);

        if (seeded.current) {
          const fresh = snap.docChanges().filter((c) => c.type === 'added').map((c) => c.doc.id);
          if (fresh.length) {
            setFlash((p) => new Set([...p, ...fresh]));
            setTimeout(() => setFlash((p) => {
              const n = new Set(p); fresh.forEach((id) => n.delete(id)); return n;
            }), FLASH_MS);
          }
        }
        seeded.current = true;
        setLeads(next); setLastAt(Date.now()); setLoading(false); setErr(null);
      },
      (e) => { setErr(e.message); setLoading(false); }
    );
    return () => unsub();
  }, []);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 10000); return () => clearInterval(t); }, []);

  // rolling 31-day total, counted on the server
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [mSnap, dSnap] = await Promise.all([
          getCountFromServer(query(
            collectionGroup(db, 'leads'),
            where('createdAt', '>=', Timestamp.fromMillis(Date.now() - MONTH_MS))
          )),
          getCountFromServer(query(
            collectionGroup(db, 'leads'),
            where('createdAt', '>=', Timestamp.fromMillis(Date.now() - DAY_MS))
          )),
        ]);
        if (alive) { setMonth(mSnap.data().count); setDayExact(dSnap.data().count); }
      } catch { /* leave the previous value on the screen */ }
    };
    load();
    const t = setInterval(load, MONTH_REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Per-client 7-day totals. These CANNOT come from the live buffer: at ~380
  // leads/day a 1500-lead buffer only reaches back about 3.5 days, so counting it
  // would silently undercount. Every lead in a SwiftPro or Michael & Son market
  // carries a `client` stamp (verified: 0 unstamped in those markets), so an
  // equality+range count is exact.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const since = Timestamp.fromMillis(Date.now() - 7 * DAY_MS);
        const [sp, ms] = await Promise.all(['SwiftPro', 'MichaelAndSon'].map((c) =>
          getCountFromServer(query(collectionGroup(db, 'leads'),
            where('client', '==', c), where('createdAt', '>=', since)))));
        if (alive) setClientWeek({
          SwiftPro: Number(sp?.data()?.count) || 0,
          MichaelAndSon: Number(ms?.data()?.count) || 0,
        });
      } catch { /* index still building, or offline — fall back to the buffer count */ }
    };
    load();
    const t = setInterval(load, MONTH_REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Daily totals for the last DAILY_DAYS complete days plus today-so-far.
  // Counted on the server for the same reason the 31-day tile is: the live
  // buffer only reaches back ~24h, so anything older cannot come from it.
  // Refetched whole on each tick so the day boundary rolls over on its own —
  // this screen stays up for days at a time.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d0 = new Date(); d0.setHours(0, 0, 0, 0);
        const days = [];
        for (let i = DAILY_DAYS; i >= 0; i--) {
          const s = new Date(d0); s.setDate(s.getDate() - i);
          const e = new Date(s); e.setDate(e.getDate() + 1);
          days.push({ start: s.getTime(), end: e.getTime(), partial: i === 0 });
        }
        const snaps = await Promise.all(days.map((d) => getCountFromServer(query(
          collectionGroup(db, 'leads'),
          where('createdAt', '>=', Timestamp.fromMillis(d.start)),
          where('createdAt', '<', Timestamp.fromMillis(d.end)),
        ))));
        if (alive) setDaily(days.map((d, i) => ({ ...d, count: Number(snaps[i]?.data()?.count) || 0 })));

        // "Is today low?" cannot be answered by extrapolating from the morning —
        // leads do not arrive at a constant rate, so at 8am a linear projection is
        // mostly noise. Instead ask how far the SAME weekday had got by this exact
        // time of day, which is a real measurement with no extrapolation in it.
        // Align the peer windows on the CLOCK, not on elapsed milliseconds. The
        // old `ps + (Date.now() - d0)` added today's real elapsed time to the
        // peer's midnight, which is only the same time of day when both days have
        // the same length. On 2026-11-01 — DST ending, so a 25-hour day — 13.4
        // real hours had passed at 12:26 local, so each peer window ran to 1:26pm
        // while today's ran to 12:26pm. That handed every peer a free hour of the
        // busiest part of the morning (11am averages 26 leads) and reported today
        // ~19% down when a clock-aligned count had it 6% UP on the day before.
        const nowRef = new Date();
        const peers = [];
        for (let k = 1; k <= 4; k += 1) {
          const ps = new Date(d0); ps.setDate(ps.getDate() - 7 * k);
          peers.push({
            start: ps.getTime(),
            byNow: sameClockTime(ps.getTime(), nowRef),
            end: nextMidnight(ps.getTime()),
          });
        }
        const peerSnaps = await Promise.all(peers.flatMap((p) => [
          getCountFromServer(query(collectionGroup(db, 'leads'),
            where('createdAt', '>=', Timestamp.fromMillis(p.start)),
            where('createdAt', '<', Timestamp.fromMillis(p.byNow)))),
          getCountFromServer(query(collectionGroup(db, 'leads'),
            where('createdAt', '>=', Timestamp.fromMillis(p.start)),
            where('createdAt', '<', Timestamp.fromMillis(p.end)))),
        ]));
        if (alive) setPeers(peers.map((p, i) => ({
          byNow: Number(peerSnaps[i * 2]?.data()?.count) || 0,
          full: Number(peerSnaps[i * 2 + 1]?.data()?.count) || 0,
        })));
      } catch { /* leave the previous bars on the screen */ }
    };
    load();
    const t = setInterval(load, MONTH_REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Today is only part of a day, so comparing its raw count against full days
  // would always read low until the evening. Project it by the fraction of the
  // day elapsed and compare THAT against the 7-day average — that is the
  // "are we running hot or cold today" number.
  const dailyStats = useMemo(() => {
    if (daily.length < 2) return null;
    const done = daily.filter((d) => !d.partial);
    const cur = daily.find((d) => d.partial);
    const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);

    // Lead volume has a strong weekly shape — Mondays run high, Sundays low — so a
    // flat all-days average makes every Sunday look like a bad day and every Monday
    // like a win. Compare each day against the SAME weekday instead.
    const byDow = {};
    done.forEach((d) => {
      const k = new Date(d.start).getDay();
      (byDow[k] = byDow[k] || []).push(d.count);
    });
    const dowAvg = {};
    Object.keys(byDow).forEach((k) => { dowAvg[k] = mean(byDow[k]); });

    const todayDow = cur ? new Date(cur.start).getDay() : new Date().getDay();
    const todayNorm = dowAvg[todayDow] || mean(done.map((d) => d.count));
    const samples = (byDow[todayDow] || []).length;

    const elapsed = cur ? scrapeDayFraction(now) : 1;
    const drift = cur ? clockDriftMin(now, cur.start) : 0;
    // How much of a normal same-weekday had landed by this time of day. Projecting
    // with THAT ratio respects the real shape of the day (slow dawn, busy evening)
    // instead of pretending leads arrive at a constant rate.
    const pByNow = peers.reduce((a, b) => a + b.byNow, 0) / Math.max(1, peers.length);
    const pFull = peers.reduce((a, b) => a + b.full, 0) / Math.max(1, peers.length);
    const shape = peers.length && pFull > 0 ? pByNow / pFull : 0;
    const pace = cur
      ? (shape > 0.02 ? cur.count / shape : cur.count / Math.max(0.08, elapsed))
      : 0;
    // The honest headline this early in the day: a straight comparison, no maths.
    const vsNow = pByNow > 0 && cur ? ((cur.count - pByNow) / pByNow) * 100 : null;
    const peakRaw = Math.max(1, ...daily.map((d) => d.count), Math.round(pace));
    // A single bad count would turn peak into NaN and every bar height with it,
    // so everything that reaches a CSS value is forced finite here.
    const fin = (n, f = 0) => (Number.isFinite(n) ? n : f);
    const peak = Math.max(1, fin(peakRaw, 1));
    const norm = fin(todayNorm, 0);
    return { done, cur, dowAvg, todayDow, todayNorm: norm, samples,
      elapsed, drift, pace: fin(pace, 0), peak,
      byNow: cur ? cur.count : 0, peerByNow: fin(pByNow, 0), peerCount: peers.length,
      vsNow: vsNow == null ? null : fin(vsNow, 0),
      vs: norm ? ((fin(pace, 0) - norm) / norm) * 100 : 0 };
  }, [daily, peers, now]);

  const today = startOfToday();
  const stats = useMemo(() => {
    const hourAgo = now - 3600e3, dayAgo = now - 864e5;
    const todays = leads.filter((l) => l.at >= today);
    const byMarket = {}, byService = {};
    for (const l of leads.filter((x) => x.at >= dayAgo)) {
      byMarket[l.market] = (byMarket[l.market] || 0) + 1;
      const s = l.serviceType || l.vertical || 'Other';
      byService[s] = (byService[s] || 0) + 1;
    }
    const hours = Array.from({ length: 24 }, () => 0);
    for (const l of leads) {
      const d = Math.floor((now - l.at) / 3600e3);
      if (d >= 0 && d < 24) hours[23 - d] += 1;
    }
    return {
      today: todays.length,
      lastHour: leads.filter((l) => l.at >= hourAgo).length,
      day: leads.filter((l) => l.at >= dayAgo).length,
      markets: Object.entries(byMarket).sort((a, b) => b[1] - a[1]),
      services: Object.entries(byService).sort((a, b) => b[1] - a[1]).slice(0, 8),
      hours,
      byClient: leads.reduce((a, l) => { if (l.clientId) a[l.clientId] = (a[l.clientId] || 0) + 1; return a; }, {}),
    };
  }, [leads, now, today]);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (clientFilter !== 'all' && l.clientId !== clientFilter) return false;
      if (marketFilter !== 'all' && l.market !== marketFilter) return false;
      if (!s) return true;
      return [l.text, l.author, l.neighborhood, l.serviceType, l.market]
        .some((v) => String(v || '').toLowerCase().includes(s));
    }).slice(0, 100);
  }, [leads, marketFilter, clientFilter, search]);

  const cToday = useCountUp(stats.today);
  const cHour = useCountUp(stats.lastHour);
  // prefer the server's exact count; fall back to the buffer before it loads
  const cDay = useCountUp(dayExact ?? stats.day);
  const cMonth = useCountUp(month ?? 0);
  // the ranking is computed from the buffer, so say so when it can't hold 24h
  const truncated = dayExact != null && dayExact > stats.day + 2;
  const peak = Math.max(1, ...stats.hours);
  const live = lastAt && now - lastAt < 180000;

  return (
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.gridBg} />
      <div style={S.orbA} className="lf-drift" />
      <div style={S.orbB} className="lf-drift2" />
      <div style={S.scan} className="lf-scan" />

      <div style={S.shell}>
        {/* ── header ── */}
        <header style={S.header}>
          <div style={S.brandRow}>
            <div style={S.logoChip}><img src={leadfeedLogo} alt="LeadFeed" style={S.logo} /></div>
            <div>
              <h1 style={S.h1}>LIVE&nbsp;FEED</h1>
              <div style={S.h1sub}>every lead, the moment it lands</div>
            </div>
          </div>
          <div style={S.statusPill}>
            <span style={S.ring} className={live ? 'lf-ring' : ''}>
              <span style={{ ...S.dot, background: live ? G.neon : G.mute }} />
            </span>
            <span style={S.statusTxt}>{live ? 'LIVE' : 'SYNCING'}</span>
            <span style={S.statusMeta}>{leads.length} in buffer</span>
          </div>
        </header>

        {err && <div style={S.err}>{err}</div>}

        {/* ── counters ── */}
        <section style={S.counters}>
          <Metric label="LEADS TODAY" value={cToday} hero />
          <Metric label="LAST HOUR"   value={cHour} />
          <Metric label="LAST 24H"    value={cDay} />
          <Metric label="LAST 31 DAYS" value={cMonth} />
        </section>

        {/* ── activity ── */}
        <section style={S.panel}>
          <div style={S.panelHead}>
            <span style={S.panelTitle}>ACTIVITY</span>
            <span style={S.panelNote}>24h · peak {peak}/hr</span>
          </div>
          <div style={S.hist}>
            {stats.hours.map((v, i) => {
              const cur = i === 23;
              return (
                <div key={i} style={S.histCol} title={`${v} lead${v === 1 ? '' : 's'} · ${23 - i}h ago`}>
                  <div
                    className="lf-bar"
                    style={{
                      ...S.bar,
                      height: `${Math.max(2, (v / peak) * 100)}%`,
                      background: cur
                        ? `linear-gradient(180deg, ${G.neon}, ${G.brand})`
                        : 'linear-gradient(180deg, rgba(0,232,95,.42), rgba(0,232,95,.06))',
                      boxShadow: cur ? `0 0 18px ${G.neon}` : 'none',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </section>

        {/* ── daily totals ── is today running hot or cold against a normal day? ── */}
        {dailyStats && (
          <section style={S.panel}>
            <div style={S.panelHead}>
              <span style={S.panelTitle}>DAILY</span>
              <span style={S.panelNote}>
                {dailyStats.vsNow == null ? (
                  <>{DOW[dailyStats.todayDow]} · usual {Math.round(dailyStats.todayNorm)}/day</>
                ) : (
                  <>
                    {/* Measured, not extrapolated, and the baseline is the SAME
                        WEEKDAY only — the last 4 Sundays if today is a Sunday —
                        each counted to this same time of day. A blended all-days
                        average would mark every Sunday down and every Monday up,
                        since weekends run ~19% below weekdays. The sample count is
                        on screen so the basis is never in doubt. */}
                    <span style={{ color: G.text }}>{dailyStats.byNow}</span> by now · usual{' '}
                    {DOW[dailyStats.todayDow]} <span style={{ opacity: .6 }}>({dailyStats.peerCount}wk avg)</span>{' '}
                    <span style={{ color: G.text }}>{Math.round(dailyStats.peerByNow)}</span>{' '}
                    <span style={{ color: dailyStats.vsNow >= 0 ? G.neon : G.amber }}>
                      {dailyStats.vsNow >= 0 ? '▲' : '▼'}{Math.abs(Math.round(dailyStats.vsNow))}%
                    </span>
                    <span style={{ opacity: .65 }}>
                      {'  ·  heading for '}{Math.round(dailyStats.pace)} vs {Math.round(dailyStats.todayNorm)}
                    </span>
                    {/* Only on the two DST days a year. Without it the percentage
                        quietly changes basis for a day and reads like a real drop. */}
                    {Math.abs(dailyStats.drift) >= 30 && (
                      <span style={{ ...S.dstNote, color: G.cyan }}
                        title={`Clocks moved ${dailyStats.drift > 0 ? 'back' : 'forward'} today, so this day is `
                          + `${24 + dailyStats.drift / 60} hours long. Counts are matched on the clock — both `
                          + `windows end at the same time of day — so the comparison stays honest.`}>
                        {'  ·  DST: clock-aligned'}
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
            <div style={S.dailyWrap}>
              {/* 7-day average, so the bars have something to be measured against */}
              <div style={{ ...S.avgLine, bottom: 26 + (dailyStats.todayNorm / dailyStats.peak) * 70 }} />
              <div style={S.dailyRow}>
                {daily.map((d) => {
                  const h = Math.max(2, (d.count / dailyStats.peak) * 100);
                  // A bare day-of-month under a bar reads as a VALUE — on Oct 6 the
                  // tick said "6" and was mistaken for 6 leads. A weekday letter cannot
                  // be misread as a count, and it makes the weekly rhythm legible too.
                  const label = DOW[new Date(d.start).getDay()][0];
                  return (
                    <div
                      key={d.start}
                      style={S.dailyCol}
                      title={(() => {
                        const dw = new Date(d.start).getDay();
                        const norm = dailyStats.dowAvg[dw];
                        const when = new Date(d.start).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                        const delta = norm ? Math.round(((d.count - norm) / norm) * 100) : null;
                        return `${d.count} lead${d.count === 1 ? '' : 's'} · ${when}${d.partial ? ' (so far today)' : ''}`
                          + (norm && !d.partial ? ` · usual ${DOW[dw]} ${Math.round(norm)} (${delta >= 0 ? '+' : ''}${delta}%)` : '');
                      })()}
                    >
                      <div style={S.dailyBarBox}>
                        {d.partial ? (
                          // Today is a part-day. Drawing only what has landed so far
                          // would sit far below the average line all morning and read
                          // as a collapse. So the outline is where today is PROJECTED
                          // to finish and the solid fill is what has actually landed.
                          <div
                            style={{
                              ...S.bar,
                              height: `${Math.max(3, (dailyStats.pace / dailyStats.peak) * 100)}%`,
                              border: `1px dashed ${G.neon}`,
                              display: 'flex',
                              alignItems: 'flex-end',
                              boxShadow: `0 0 18px rgba(0,232,95,.35)`,
                            }}
                          >
                            <div
                              className="lf-bar"
                              style={{
                                ...S.bar,
                                height: `${Math.min(100, (d.count / Math.max(1, dailyStats.pace)) * 100)}%`,
                                background: `linear-gradient(180deg, ${G.neon}, ${G.brand})`,
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="lf-bar"
                            style={{
                              ...S.bar,
                              height: `${h}%`,
                              background: 'linear-gradient(180deg, rgba(0,232,95,.42), rgba(0,232,95,.06))',
                            }}
                          />
                        )}
                      </div>
                      <span style={{ ...S.dailyTick, color: d.partial ? G.neon : G.faint }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <div style={S.split}>
          {/* ── feed ── */}
          <section style={S.panel}>
            <div style={S.panelHead}>
              <span style={S.panelTitle}>INCOMING</span>
              <span style={{ ...S.panelNote, marginLeft: 6 }}>7d</span>
              <div style={S.clientTabs}>
                {[['all', 'ALL'], ['SwiftPro', CLIENT_LABEL.SwiftPro], ['MichaelAndSon', CLIENT_LABEL.MichaelAndSon]].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setClientFilter(k)}
                    style={{
                      ...S.clientTab,
                      color: clientFilter === k ? '#00120A' : G.neon,
                      background: clientFilter === k ? G.neon : 'transparent',
                    }}
                  >{label}{k !== 'all' ? ` ${(clientWeek && clientWeek[k] != null) ? clientWeek[k] : (stats.byClient[k] || 0)}` : ''}</button>
                ))}
              </div>
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="search…" style={S.search} className="lf-input"
              />
            </div>

            {/*
              One line, not a wrapping cluster. ALL stays pinned so the filter
              can always be cleared without waiting for it to scroll past; the
              markets ride a marquee that pauses on hover so a chip can be
              clicked. The track renders the chips TWICE and translates -50%,
              which is what makes the loop seamless — with a single copy the
              row would visibly snap back at the end.
            */}
            <div style={S.chipBar}>
              <Chip on={marketFilter === 'all'} onClick={() => setMarketFilter('all')}>ALL · {leads.length}</Chip>
              <div style={S.marqueeMask}>
                <div
                  className="lf-marquee"
                  style={{ ...S.marqueeTrack, animationDuration: `${Math.max(24, stats.markets.length * 4.5)}s` }}
                >
                  {[...stats.markets, ...stats.markets].map(([m, c], i) => (
                    <Chip
                      key={`${m}-${i < stats.markets.length ? 'a' : 'b'}`}
                      on={marketFilter === m}
                      onClick={() => setMarketFilter(m)}
                    >{m} · {c}</Chip>
                  ))}
                </div>
              </div>
            </div>

            <div style={S.feed} className="lf-scroll">
              {loading && <div style={S.empty}>establishing link…</div>}
              {!loading && !visible.length && <div style={S.empty}>no leads match</div>}
              {/*
                No `layout` prop and no height-based exit here on purpose: with
                a list that reorders on every snapshot, framer-motion's layout
                measurement plus an animated height collapsed the cards to
                hairlines. Opacity/translate only, and the card carries a
                minHeight so it can never render as a bare line.
              */}
              <AnimatePresence initial={false}>
                {visible.map((l) => {
                  const isNew = flash.has(l.id);
                  return (
                    <motion.article
                      key={l.path}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                      style={{
                        ...S.card,
                        borderColor: isNew ? 'rgba(0,232,95,.55)' : 'rgba(0,232,95,.12)',
                        boxShadow: isNew
                          ? '0 0 0 1px rgba(0,232,95,.32), 0 0 34px rgba(0,232,95,.2)'
                          : 'none',
                      }}
                    >
                      <span style={{ ...S.rail, opacity: isNew ? 1 : 0.3 }} />
                      <div style={S.cardBody}>
                        <div style={S.metaRow}>
                          <span style={S.mkt}>{l.market}</span>
                          {l.clientId && CLIENT_STYLE[l.clientId] && (
                            <span style={{
                              ...S.clientTag,
                              color: CLIENT_STYLE[l.clientId].color,
                              borderColor: `${CLIENT_STYLE[l.clientId].color}55`,
                              background: `${CLIENT_STYLE[l.clientId].color}14`,
                            }}>{CLIENT_STYLE[l.clientId].label}</span>
                          )}
                          {(l.serviceType || l.vertical) && (
                            <span style={S.svcTag}>{l.serviceType || l.vertical}</span>
                          )}
                          {l.phone && <span style={S.phoneTag}>☎ {l.phone}</span>}
                          {isNew && <span style={S.newTag} className="lf-blink">NEW</span>}
                          <span style={S.age}>{ago(l.at, now)}</span>
                        </div>
                        <p style={S.body}>{l.text || '—'}</p>
                        <div style={S.footRow}>
                          <span style={S.who}>{l.author || 'Unknown'}</span>
                          {l.neighborhood && <span style={S.hood}>{l.neighborhood}</span>}
                          {(l.permalink || l.postUrl) && (
                            <a href={l.permalink || l.postUrl} target="_blank" rel="noreferrer" style={S.open}>
                              OPEN ↗
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </div>
          </section>

          {/* ── rail ── */}
          <aside>
            <section style={S.panel}>
              <div style={S.panelHead}>
                <span style={S.panelTitle}>HOT MARKETS</span>
                <span style={{ ...S.panelNote, color: truncated ? G.amber : G.mute }}>
                  {truncated ? `24h · showing newest ${stats.day} of ${dayExact}` : '24h'}
                </span>
              </div>
              <div style={S.railBody}>
                {!stats.markets.length && <div style={S.empty}>quiet</div>}
                {stats.markets.slice(0, 12).map(([m, c], i) => (
                  <div key={m} style={S.rankRow}>
                    <span style={S.rankNo}>{String(i + 1).padStart(2, '0')}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.rankTop}>
                        <span style={S.rankName}>{m}</span>
                        <span style={S.rankVal}>{c}</span>
                      </div>
                      <div style={S.track}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(c / stats.markets[0][1]) * 100}%` }}
                          transition={{ type: 'spring', stiffness: 110, damping: 24 }}
                          style={S.fill}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={S.panel}>
              <div style={S.panelHead}><span style={S.panelTitle}>SERVICE MIX</span><span style={S.panelNote}>24h</span></div>
              <div style={S.railBody}>
                {!stats.services.length && <div style={S.empty}>quiet</div>}
                {stats.services.map(([s, c]) => (
                  <div key={s} style={S.svcRow}>
                    <span style={S.svcName}>{s}</span>
                    <div style={S.svcTrack}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(c / stats.services[0][1]) * 100}%` }}
                        transition={{ type: 'spring', stiffness: 110, damping: 24 }}
                        style={S.svcFill}
                      />
                    </div>
                    <span style={S.svcVal}>{c}</span>
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

/* ── pieces ─────────────────────────────────────────────────────────────── */
function Metric({ label, value, hero, accent }) {
  const c = accent || G.neon;
  return (
    <div style={{ ...S.metric }} className="lf-card">
      <div style={{ ...S.metricGlow, background: `radial-gradient(80% 120% at 20% 0%, ${c}1f, transparent 70%)` }} />
      <div style={{ ...S.metricVal, color: c, fontSize: hero ? 64 : 38, textShadow: `0 0 26px ${c}66` }}>
        {value}
      </div>
      <div style={S.metricLbl}>{label}</div>
      <span style={{ ...S.corner, borderColor: `${c}55` }} />
    </div>
  );
}

function Chip({ on, onClick, children }) {
  return (
    <button onClick={onClick} className="lf-chip" style={{
      ...S.chip,
      color: on ? '#00120A' : G.neon,
      background: on ? G.neon : 'rgba(0,232,95,.07)',
      borderColor: on ? G.neon : 'rgba(0,232,95,.22)',
      boxShadow: on ? `0 0 18px ${G.neon}66` : 'none',
    }}>{children}</button>
  );
}

/* ── css ────────────────────────────────────────────────────────────────── */
const CSS = `
@keyframes lfRing { 0%{box-shadow:0 0 0 0 rgba(0,232,95,.55)} 70%{box-shadow:0 0 0 11px rgba(0,232,95,0)} 100%{box-shadow:0 0 0 0 rgba(0,232,95,0)} }
.lf-ring { animation: lfRing 2s cubic-bezier(.4,0,.6,1) infinite; border-radius:50%; }
@keyframes lfBlink { 0%,100%{opacity:1} 50%{opacity:.32} }
.lf-blink { animation: lfBlink 1.1s ease-in-out infinite; }
@keyframes lfDrift { 0%,100%{transform:translate(0,0)} 50%{transform:translate(60px,-40px)} }
.lf-drift { animation: lfDrift 22s ease-in-out infinite; }
@keyframes lfDrift2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-70px,50px)} }
.lf-drift2 { animation: lfDrift2 28s ease-in-out infinite; }
@keyframes lfScan { 0%{transform:translateY(-160px)} 100%{transform:translateY(100vh)} }
.lf-scan { animation: lfScan 7s linear infinite; }
.lf-bar { transition: height .6s cubic-bezier(.22,1,.36,1); }
.lf-chip:hover { filter: brightness(1.3); }
@keyframes lfMarquee { from { transform: translate3d(0,0,0) } to { transform: translate3d(-50%,0,0) } }
.lf-marquee { animation-name: lfMarquee; animation-timing-function: linear; animation-iteration-count: infinite; }
.lf-marquee:hover { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) { .lf-marquee { animation: none; } }
.lf-input::placeholder { color:#3D5C4A; }
.lf-input:focus { border-color: rgba(0,232,95,.5) !important; box-shadow: 0 0 16px rgba(0,232,95,.18); }
.lf-scroll::-webkit-scrollbar { width:7px }
.lf-scroll::-webkit-scrollbar-thumb { background:rgba(0,232,95,.22); border-radius:8px }
.lf-scroll::-webkit-scrollbar-track { background:transparent }
.lf-card { transition: transform .25s ease; }
.lf-card:hover { transform: translateY(-2px); }
@media (max-width: 1080px) { .lf-split { grid-template-columns: 1fr !important; } }
`;

/* ── styles ─────────────────────────────────────────────────────────────── */
const mono = "'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace";
const S = {
  page: { position: 'relative', minHeight: '100vh', background: '#000', color: G.text, overflow: 'hidden', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' },
  gridBg: { position: 'fixed', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(0,232,95,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(0,232,95,.045) 1px, transparent 1px)', backgroundSize: '56px 56px', maskImage: 'radial-gradient(circle at 50% 0%, #000 0%, transparent 78%)', WebkitMaskImage: 'radial-gradient(circle at 50% 0%, #000 0%, transparent 78%)' },
  orbA: { position: 'fixed', top: -180, left: -120, width: 620, height: 620, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,232,95,.16), transparent 62%)', filter: 'blur(42px)', pointerEvents: 'none' },
  orbB: { position: 'fixed', bottom: -220, right: -140, width: 660, height: 660, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,120,24,.3), transparent 62%)', filter: 'blur(48px)', pointerEvents: 'none' },
  scan: { position: 'fixed', left: 0, right: 0, height: 160, pointerEvents: 'none', background: 'linear-gradient(180deg, transparent, rgba(0,232,95,.05), transparent)' },
  shell: { position: 'relative', maxWidth: 1560, margin: '0 auto', padding: '24px 28px 48px' },

  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 22 },
  brandRow: { display: 'flex', alignItems: 'center', gap: 15 },
  logoChip: { background: '#fff', borderRadius: 13, padding: '9px 13px', boxShadow: '0 0 30px rgba(0,232,95,.28)' },
  logo: { height: 25, display: 'block' },
  h1: { margin: 0, fontSize: 25, fontWeight: 800, letterSpacing: '.16em', color: '#fff', textShadow: `0 0 28px ${G.neon}55` },
  h1sub: { fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: G.mute, marginTop: 4 },
  statusPill: { display: 'flex', alignItems: 'center', gap: 11, border: '1px solid rgba(0,232,95,.24)', background: 'rgba(0,232,95,.05)', borderRadius: 999, padding: '9px 17px', backdropFilter: 'blur(8px)' },
  ring: { width: 9, height: 9, borderRadius: '50%', display: 'inline-flex' },
  dot: { width: 9, height: 9, borderRadius: '50%' },
  statusTxt: { fontFamily: mono, fontSize: 11.5, fontWeight: 700, letterSpacing: '.18em', color: G.neon },
  statusMeta: { fontFamily: mono, fontSize: 11, color: G.mute, borderLeft: '1px solid rgba(0,232,95,.2)', paddingLeft: 11 },
  err: { border: '1px solid rgba(255,90,90,.4)', background: 'rgba(255,90,90,.1)', color: '#ffb4b4', padding: '10px 14px', borderRadius: 10, fontSize: 12.5, marginBottom: 16, fontFamily: mono },

  counters: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, marginBottom: 16 },
  metric: { position: 'relative', overflow: 'hidden', borderRadius: 18, border: '1px solid rgba(0,232,95,.14)', background: 'linear-gradient(160deg, rgba(0,28,12,.72), rgba(0,10,5,.6))', backdropFilter: 'blur(12px)', padding: '20px 22px 18px' },
  metricGlow: { position: 'absolute', inset: 0, pointerEvents: 'none' },
  metricVal: { position: 'relative', fontFamily: mono, fontWeight: 700, lineHeight: 1, letterSpacing: '-.02em' },
  metricLbl: { position: 'relative', marginTop: 9, fontSize: 10.5, letterSpacing: '.2em', color: G.mute, fontWeight: 700 },
  corner: { position: 'absolute', top: 12, right: 12, width: 12, height: 12, borderTop: '1.5px solid', borderRight: '1.5px solid', borderRadius: '0 4px 0 0' },

  panel: { position: 'relative', borderRadius: 18, border: '1px solid rgba(0,232,95,.12)', background: 'linear-gradient(160deg, rgba(0,22,10,.66), rgba(0,8,4,.58))', backdropFilter: 'blur(12px)', marginBottom: 14, overflow: 'hidden' },
  panelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '15px 18px 11px', borderBottom: '1px solid rgba(0,232,95,.08)' },
  panelTitle: { fontFamily: mono, fontSize: 11.5, fontWeight: 700, letterSpacing: '.2em', color: G.neon },
  panelNote: { fontFamily: mono, fontSize: 10.5, color: G.mute, letterSpacing: '.08em' },
  dstNote: { fontFamily: mono, fontSize: 10, letterSpacing: '.1em', opacity: .9, cursor: 'help' },

  hist: { display: 'flex', alignItems: 'flex-end', gap: 4, height: 104, padding: '16px 18px 18px' },
  // same 70px bar area as `hist`, plus a row for the date ticks underneath
  dailyWrap: { position: 'relative', padding: '16px 18px 10px', height: 112 },
  dailyRow: { display: 'flex', alignItems: 'flex-end', gap: 4, height: '100%' },
  dailyCol: { flex: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' },
  dailyBarBox: { flex: 1, display: 'flex', alignItems: 'flex-end' },
  dailyTick: { fontFamily: mono, fontSize: 9, textAlign: 'center', paddingTop: 4, letterSpacing: '.02em' },
  avgLine: { position: 'absolute', left: 18, right: 18, borderTop: '1px dashed rgba(0,232,95,.35)', pointerEvents: 'none' },
  histCol: { flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' },
  bar: { width: '100%', borderRadius: '3px 3px 1px 1px' },

  split: { display: 'grid', gridTemplateColumns: 'minmax(0,1.65fr) minmax(310px,.85fr)', gap: 14, alignItems: 'start' },

  search: { fontFamily: mono, background: 'rgba(0,0,0,.5)', border: '1px solid rgba(0,232,95,.18)', borderRadius: 9, color: G.text, padding: '7px 12px', fontSize: 12, width: 200, outline: 'none' },
  chipBar: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', minWidth: 0 },
  // fades the chips out at both ends so they slide in and out of nothing
  marqueeMask: {
    flex: 1, minWidth: 0, overflow: 'hidden',
    maskImage: 'linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent)',
    WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent)',
  },
  marqueeTrack: { display: 'flex', gap: 6, width: 'max-content', willChange: 'transform' },
  clientTabs: { display: 'flex', gap: 2, marginLeft: 'auto', border: '1px solid rgba(0,232,95,.22)', borderRadius: 8, padding: 2, background: 'rgba(0,0,0,.4)' },
  clientTab: { fontFamily: mono, fontSize: 10, fontWeight: 700, letterSpacing: '.08em', border: 'none', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .18s' },
  chip: { fontFamily: mono, border: '1px solid', borderRadius: 999, padding: '4px 11px', fontSize: 10.5, letterSpacing: '.06em', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600, transition: 'all .2s' },

  feed: { maxHeight: 660, overflowY: 'auto', padding: '2px 18px 18px', display: 'flex', flexDirection: 'column', gap: 10 },
  empty: { fontFamily: mono, color: G.faint, fontSize: 12, padding: '34px 0', textAlign: 'center', letterSpacing: '.1em' },

  // minHeight guards against a card ever rendering as a bare hairline.
  card: { position: 'relative', display: 'flex', minHeight: 76, borderRadius: 13, border: '1px solid', background: 'rgba(0,14,7,.62)', overflow: 'hidden', transition: 'border-color .6s, box-shadow .6s', flexShrink: 0 },
  rail: { width: 2, flexShrink: 0, background: `linear-gradient(180deg, ${G.neon}, transparent)` },
  cardBody: { flex: 1, minWidth: 0, padding: '12px 15px' },
  metaRow: { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 7 },
  mkt: { fontFamily: mono, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: G.neon, background: 'rgba(0,232,95,.1)', border: '1px solid rgba(0,232,95,.26)', borderRadius: 5, padding: '2px 7px', textTransform: 'uppercase' },
  clientTag: { fontFamily: mono, fontSize: 9.5, fontWeight: 800, letterSpacing: '.12em', border: '1px solid', borderRadius: 5, padding: '2px 7px' },
  svcTag: { fontFamily: mono, fontSize: 10, color: G.mute, border: '1px solid rgba(0,232,95,.14)', borderRadius: 5, padding: '2px 7px' },
  phoneTag: { fontFamily: mono, fontSize: 10, fontWeight: 700, color: G.amber, background: 'rgba(255,197,61,.1)', border: '1px solid rgba(255,197,61,.32)', borderRadius: 5, padding: '2px 7px' },
  newTag: { fontFamily: mono, fontSize: 9, fontWeight: 800, letterSpacing: '.16em', color: '#00120A', background: G.neon, borderRadius: 4, padding: '2px 6px' },
  age: { fontFamily: mono, marginLeft: 'auto', fontSize: 10.5, color: G.faint, letterSpacing: '.06em' },
  body: { margin: 0, fontSize: 13, lineHeight: 1.6, color: '#BFE3CE', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  footRow: { display: 'flex', alignItems: 'center', gap: 9, marginTop: 9 },
  who: { fontSize: 11.5, fontWeight: 600, color: G.mute },
  hood: { fontSize: 11.5, color: G.faint },
  open: { fontFamily: mono, marginLeft: 'auto', fontSize: 10, letterSpacing: '.12em', color: G.neon, textDecoration: 'none', border: '1px solid rgba(0,232,95,.24)', borderRadius: 5, padding: '3px 8px' },

  railBody: { padding: '12px 18px 16px' },
  rankRow: { display: 'flex', alignItems: 'center', gap: 11, padding: '7px 0' },
  rankNo: { fontFamily: mono, fontSize: 10, color: G.faint, width: 18 },
  rankTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 },
  rankName: { fontSize: 12.5, fontWeight: 600, color: '#CFEBDA' },
  rankVal: { fontFamily: mono, fontSize: 12, fontWeight: 700, color: G.neon },
  track: { height: 4, background: 'rgba(0,232,95,.08)', borderRadius: 3, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 3, background: `linear-gradient(90deg, ${G.neon}, ${G.brand})`, boxShadow: `0 0 12px ${G.neon}77` },

  svcRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' },
  svcName: { fontSize: 11.5, color: G.mute, width: 112, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  svcTrack: { flex: 1, height: 4, background: 'rgba(0,232,95,.08)', borderRadius: 3, overflow: 'hidden' },
  svcFill: { height: '100%', borderRadius: 3, background: `linear-gradient(90deg, ${G.cyan}, ${G.neon})` },
  svcVal: { fontFamily: mono, fontSize: 11, fontWeight: 700, color: '#CFEBDA', width: 24, textAlign: 'right' },
};
